import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { StackedSidebarService } from '../../../../services/stacked-sidebar.service';
import { SidebarType } from '../../../../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../services/notifications.service';
import { BaseService } from '../../../../services/base.service';
import { IntegrationService } from '../../../../services/integration.service';
import { AppTableSource, ColumnSortType } from '../../../../model/table.model';
import { tableHeaders } from './my-planners.interface';
import { IParams } from '../../../../model/http';
import { initItemsParams, OlxAdvertService } from '../../../../services/olx-advert.service';
import { SelectionModel } from '@angular/cdk/collections';
import { IError } from '../../../../interfaces';
import {
  ADVERT_PLANNER_STATUS,
  CHANNEL_STATUS,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
  MODAL_TYPES,
} from '../../../../enum/constants';
import { ModalService } from '../../../../services/modal.service';
import { DialogService } from '../../../../services/dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PlannerComponent } from '../../../../modals/planner/planner.component';
import moment from 'moment';
import { ChannelService } from '../../../../services/channel.service';

@Component({
  selector: 'app-my-planners',
  templateUrl: './my-planners.component.html',
  styleUrls: ['./my-planners.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MyPlannersComponent implements OnInit, OnDestroy {
  // Destroy relative
  private readonly destroy$: Subject<null> = new Subject<null>();

  // Outputs
  @Output() onReloadChat = new EventEmitter();

  public filterStatuses = [
    {
      name: 'olx.adverts.successfully',
      value: ['COMPLETED'],
      key: 'COMPLETED',
      color: 'green',
      active: true,
      selected: false,
    },
    {
      name: 'olx.adverts.in_progress',
      value: ['PROGRESS'],
      key: 'PROGRESS',
      color: 'gray',
      active: true,
      selected: false,
    },
    { name: 'olx.adverts.draft', value: ['DRAFT'], key: 'DRAFT', color: 'black', active: true, selected: false },
    {
      name: 'olx.adverts.waiting',
      value: ['WAITING'],
      key: 'WAITING',
      color: 'orangered',
      active: true,
      selected: false,
    },
    {
      name: 'olx.adverts.canceled',
      value: ['CANCELED'],
      key: 'CANCELED',
      color: 'orange',
      active: true,
      selected: false,
    },
    { name: 'olx.adverts.fail', value: ['FAIL'], key: 'FAIL', color: 'red', active: true, selected: false },
  ];

  public statistic: any = {};

  // DataSource
  dataSource$: Observable<AppTableSource> = this.olxAdvertService.itemsPlanners$.pipe(
    map((data) => {
      tableHeaders.map((column) => {
        column.sort = ColumnSortType.NONE;
        if (column.value === this.dataSort?.active) {
          column.sort = this.dataSort.direction;
        }
        return column;
      });
      this.statistic = data.statistic || {};
      return {
        headers: tableHeaders,
        rows: data.items?.map((v: any) => {
          return {
            advert_id: v.x_data?.advert?.id,
            advert_name: v.x_data?.advert?.title,
            images: v.x_data?.advert?.images,
            reason: v.x_data?.reason,
            planner_name: v.name,
            ...v,
          };
        }),
        pageSize: data?.size || 0,
        pageIndex: data.page ? data.page - 1 : 0,
        length: data.total || 0,
      };
    }),
  );

  public bulkActionList: any[] = [
    // { label: 'change_planning', value: 'UPDATE_PLANNERS' },
    { label: 'cancel_planning', value: 'CANCEL_PLANNERS' },
    { label: 'to_draft', value: 'TO_DRAFT' },
    { label: 'to_active', value: 'TO_ACTIVE' },
  ];

  public visibleColumns: string[] = tableHeaders.map((v) => v.value);
  public selectionTable = new SelectionModel<any>(true, []);

  private itemsRequest: any;
  private dataSort: any;
  public isLoader: boolean = false;

  public items: any[] = [];
  public currentChannelId: any = null;
  public balance: any;
  public timerSearch: any;
  private channelQuery = this.activatedRoute.snapshot.queryParams['channel'];

  constructor(
    private readonly stackedSidebarService: StackedSidebarService,
    private readonly notificationService: NotificationService,
    private readonly integrationService: IntegrationService,
    private readonly olxAdvertService: OlxAdvertService,
    private readonly dialogService: DialogService,
    private readonly modalService: ModalService,
    public readonly baseService: BaseService,
    public readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly channelService: ChannelService,
  ) {}

  ngOnInit() {
    this.isLoader = true;
    this.channelService
      .getChannels({ scope: 'apps', types: [CHANNEL_TYPE.OLX], status: CHANNEL_STATUS.CONNECTED })
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (channels: any[]) => {
          this.items = channels;
          const data = {
            id: this.channelQuery || channels[0]?.id || null,
          };
          this.onValueChange(data);
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.olxAdvertService.destroy();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private getItemsWithUpdatedParams(isLoader = false, params: IParams = {}) {
    if (this.itemsRequest) this.itemsRequest.unsubscribe();

    this.isLoader = isLoader;
    this.selectionTable.clear();

    this.itemsRequest = this.olxAdvertService
      .setQueryPlannersParams(this.currentChannelId, params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res) => {
          this.isLoader = false;
          if (!res.items.length && res.page > 1) {
            this.getItemsWithUpdatedParams(true, {
              page: res.page - 1,
              take: res.take,
            });
          }
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  private initBalance(): void {
    this.olxAdvertService
      .getBalance(this.currentChannelId)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (balance) => {
          this.balance = balance;
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public onValueChange(event: any): void {
    this.currentChannelId = event?.id;
    this.baseService.setQuery('channel', this.currentChannelId);
    this.initBalance();
    this.getItemsWithUpdatedParams(true);
  }

  public onSearchChange(search: string): void {
    if (this.timerSearch) clearTimeout(this.timerSearch);

    this.timerSearch = setTimeout(() => {
      this.getItemsWithUpdatedParams(true, {
        ...initItemsParams,
        search,
      });
    }, 500);
  }

  public onSortChange(sortState: any) {
    this.dataSort = sortState;

    const selectedFilter = this.filterStatuses.find((filter) => filter.selected);

    this.getItemsWithUpdatedParams(false, {
      sort: sortState?.active,
      order: sortState.direction,
      filters: { status: selectedFilter?.value || null },
    });
  }

  public onPaginationChange(paginationDetails: any) {
    const { pageIndex, pageSize } = paginationDetails;
    const page = pageIndex + 1;
    const take = pageSize;

    this.getItemsWithUpdatedParams(true, { page, take });
  }

  public onActionChange(event: any) {
    if (event.label === 'REPLAN_PLANNER') {
      if (event.status === ADVERT_PLANNER_STATUS.WAITING) {
        this.notificationService.showWarning(
          '',
          activeTranslations.notifications_texts.warn.action_not_possible,
        );
        return;
      }

      this.modalService
        .openModal({
          title: 're_planning',
          translate: true,
          component: PlannerComponent,
          data: {
            modalType: MODAL_TYPES.ADVERT,
            advert_id: event.advert_id,
          },
        })
        .subscribe(() => {
          this.getItemsWithUpdatedParams();
        });
    }

    // if (event.status !== ADVERT_PLANNER_STATUS.WAITING && event.type_planner !== 'PACKET') {
    //   this.notificationService.showWarning('', activeTranslations.notifications_texts.warn.action_not_possible)
    //   return;
    // }
    console.log(event);

    if (event.label === 'CHANGE_ADVERT' && event.type_planner === 'CREATE_ADVERT') {
      this.stackedSidebarService
        .openSidebar(
          {
            type: SidebarType.ChangeAdvert,
            maxWidth: '50vw',
          },
          {
            is_planner: true,
            advert: event.x_data.advert,
            channel_id: this.currentChannelId,
            type_action: 'UPDATE',
          },
        )
        .subscribe({
          next: (res) => {
            if (res === 'close') {
              this.getItemsWithUpdatedParams();
            }
          },
        });
    }

    if (event.label === 'TO_DRAFT') {
      const payload = {
        id: event.id,
        status: 'DRAFT',
      };
      this.olxAdvertService.updateStatusPacket(payload).subscribe(() => {
        this.getItemsWithUpdatedParams();
      });
    }

    if (event.label === 'TO_ACTIVE') {
      const payload = {
        id: event.id,
        status: 'WAITING',
      };
      this.olxAdvertService.updateStatusPacket(payload).subscribe(() => {
        this.getItemsWithUpdatedParams();
      });
    }

    if (event.label === 'CANCEL_PLANNER') {
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.my-planners',
      });

      dialogRef.subscribe((result: any) => {
        if (result) {
          this.olxAdvertService.cancelPlanner(event.id).subscribe({
            next: () => {
              this.getItemsWithUpdatedParams();
            },
            error: (e: any) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
        }
      });
    }
  }

  private preprocessPlanners(arr: any[]) {
    if (arr.every((v) => v.status !== ADVERT_PLANNER_STATUS.WAITING)) {
      this.notificationService.showWarning(
        '',
        activeTranslations.notifications_texts.warn.no_valid_status,
      );
      return [];
    }

    if (arr.some((v) => v.status !== ADVERT_PLANNER_STATUS.WAITING)) {
      this.notificationService.showWarning(
        '',
        activeTranslations.notifications_texts.warn.contain_invalid_statuses,
      );
    }

    return arr.filter((item) => !!item && item.status === ADVERT_PLANNER_STATUS.WAITING).map((v) => v.id);
  }

  public bulkActionPopup(event: any): void {
    // const ids = this.preprocessPlanners(this.selectionTable.selected);
    // if (!ids.length) return;
    const ids = this.selectionTable.selected.map((v) => v.id);

    if (event.value === 'UPDATE_PLANNERS') {
      this.modalService
        .openModal({
          title: 'change_the_activation_time_of_the_ads',
          translate: true,
          component: PlannerComponent,
          data: {
            modalType: MODAL_TYPES.ADVERT,
            planners: ids,
          },
        })
        .subscribe({
          next: () => {
            this.getItemsWithUpdatedParams();
          },
          error: (e) => {
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
            });
          },
        });
    }

    if (event.value === 'TO_ACTIVE') {
      this.olxAdvertService.bulkPlannersUpdate(ids, { status: 'WAITING' }).subscribe({
        next: () => {
          this.getItemsWithUpdatedParams();
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
    }

    if (event.value === 'TO_DRAFT') {
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.to_draft',
      });

      dialogRef.subscribe((result: any) => {
        if (result) {
          this.olxAdvertService.bulkPlannersUpdate(ids, { status: 'DRAFT' }).subscribe({
            next: () => {
              this.getItemsWithUpdatedParams();
            },
            error: (e: any) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
        }
      });
    }

    if (event.value === 'CANCEL_PLANNERS') {
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.my-planners',
      });

      dialogRef.subscribe((result: any) => {
        if (result) {
          this.olxAdvertService.cancelBulkPlanners(ids).subscribe({
            next: () => {
              this.getItemsWithUpdatedParams();
            },
            error: (e: any) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
        }
      });
    }
  }

  onFilterChange(event: string, item: any) {
    if (item.selected) {
      this.filterStatuses.forEach((v) => (v.active = true));
      this.filterStatuses.forEach((v) => (v.selected = false));
      this.getItemsWithUpdatedParams(true, {
        filters: { status: null },
      });
    } else {
      this.filterStatuses.forEach((v) => (v.active = false));
      this.filterStatuses.forEach((v) => (v.selected = false));
      item.active = true;
      item.selected = true;
      this.getItemsWithUpdatedParams(true, {
        filters: { status: item?.value },
      });
    }
  }

  onRowClick(event: any) {
    this.modalService
      .openModal({
        title: 'change_the_ad_activation_time',
        translate: true,
        component: PlannerComponent,
        data: {
          modalType: MODAL_TYPES.ADVERT,
          planner_id: event.id,
          info: event,
        },
      })
      .subscribe((res) => {
        const dateToString = moment(res.x_data.date).format('DD/MM/YYYY');
        const dateTime = moment(`${dateToString} ${res.x_data.time}`, 'DD/MM/YYYY HH:mm');
        const payload = {
          planned_at: dateTime,
          name: res.name,
          status: res.x_data.status,
          id: event.id,
        };
        this.olxAdvertService.updatePlanner2(payload, event).subscribe(() => {
          this.getItemsWithUpdatedParams();
        });
      });
  }
}
